import { notifyAlert, notifyFailure, notifySuccess } from 'app/Notifications/actions';
import { deletePageFailure, deletePageRequest } from 'app/Page/actions';
import { clearContentOrFolderInStore } from 'app/UserInfo/actions';
import { CONTENT_TYPES } from 'lib/const';

import {
  addFolder,
  addFolderFailure,
  addFolderSuccess,
  getFolderFailure,
  getFolderRequest,
  getFolderSuccess,
  updateFolderSuccess
} from './actions';

/**
 * get a folder
 *
 * @param {string} folderID - id of the folder
 * @returns {Promise<Function>} thunk
 */
export const getFolderThunk = folderID => async (dispatch, _, { api }) => {
  try {
    dispatch(getFolderRequest(folderID));
    const response = await api.folder.getFolder(folderID);
    dispatch(getFolderSuccess(folderID, response));
  } catch (error) {
    dispatch(getFolderFailure(folderID, error));
  }
};

/**
 * add a folder
 * @param {object} folderName - the folder name to be added
 * @param {object} parentFolderID - parent folder id for the folder
 * @returns {Function} thunk
 */
export const addFolderThunk = (folderName, parentFolderID) => async (dispatch, _, { api }) => {
  dispatch(notifyAlert(`Adding folder ${folderName}`));
  try {
    dispatch(addFolder(folderName, parentFolderID));
    const successData = await api.folder.addFolder(folderName, parentFolderID);
    dispatch(addFolderSuccess(folderName, parentFolderID, successData));
    dispatch(notifySuccess(`Folder ${folderName} is added`));
  } catch (error) {
    dispatch(addFolderFailure(folderName, parentFolderID, error));
    dispatch(notifyFailure(error.message));
  }
};

/**
 * update a folder
 * @param {string} folderId - id for the folder
 * @param {string} folderName - the folder name to be update
 * @param {string} parentFolderID - parent folder id for the folder
 * @returns {Function} thunk
 */
export const updateFolderThunk = (folderId, folderName, parentFolderID) => async (
  dispatch,
  _,
  { api }
) => {
  dispatch(notifyAlert(`Updating folder ${folderName}`));
  try {
    const successData = await api.folder.updateFolder(folderName, folderId);
    dispatch(updateFolderSuccess(folderName, parentFolderID, successData));
    dispatch(notifySuccess(`Folder ${folderName} is updated`));
  } catch (error) {
    dispatch(notifyFailure(error.message));
  }
};

/**
 * delete a folder
 * @param {string} folderId - id for the folder
 * @param {string} folderName - the folder name to be update
 * @param {string} parentFolderID - parent folder id for the folder
 * @returns {Function} thunk
 */
export const deleteFolderThunk = (folderId, folderName, parentFolderID) => async (
  dispatch,
  _,
  { api }
) => {
  dispatch(notifyAlert(`deleting folder ${folderName}`));
  try {
    await api.folder.deleteFolder(folderId);
    dispatch(clearContentOrFolderInStore(CONTENT_TYPES.FOLDER, folderId, parentFolderID));
    dispatch(notifySuccess(`Folder ${folderName} is deleted`));
  } catch (error) {
    dispatch(notifyFailure(error.message));
  }
};

/**
 * delete a page
 * @param {string} pageId - id for the page
 * @param {string} pageName - the page name to be deleted
 * @param {string} parentFolderID - parent folder id for the folder
 * @returns {Function} thunk
 */
export const deletePageThunk = (pageId, pageName, parentFolderID) => async (
  dispatch,
  getState,
  { api }
) => {
  dispatch(notifyAlert(`deleting page ${pageName}`));
  try {
    dispatch(deletePageRequest());
    await api.page.deletePage(pageId);
    dispatch(clearContentOrFolderInStore(CONTENT_TYPES.PAGE, pageId, parentFolderID));
    dispatch(notifySuccess(`Page delete request for '${pageName}' is completed.`));
  } catch (error) {
    dispatch(deletePageFailure(error));
    dispatch(notifyFailure(error.message || 'Page delete process completed with errors!'));
  }
};
